import { useRef, useState } from "react";
import { LinearProgress } from "@mui/material";
import {
  Check,
  Download,
  FileText,
  LoaderCircle,
  Pencil,
  Send,
  TriangleAlert,
} from "lucide-react";
import { toast } from "react-toastify";
import {
  ProductionPdfPreview,
  type ProductionPdfProps,
} from "./ProductionPdfPreview";
import { SigneeSelector, type Signee } from "./SigneeSelector";
import { Button } from "@/components/Button/Button";

type Props = ProductionPdfProps & {
  onDownload: (taskId: string, name: string) => void;
  signees: Signee[];
  defaultSignee?: Signee;
  onSendToSigning?: (signees: Signee[], pdfBytes: string) => Promise<boolean>;
  isSending: boolean;
  signingRequested: boolean;
  taskDescription?: string | null;
};

export function ProductionPreview({
  attachment,
  fetchAttachment,
  onDownload,
  signees,
  defaultSignee,
  onSendToSigning,
  isSending,
  signingRequested,
  taskDescription,
}: Props) {
  const [selectedSignees, setSelectedSignees] = useState<Signee[]>(() =>
    defaultSignee ? [defaultSignee] : []
  );
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const busy = isSending || submitting;
  const signingComplete = signingRequested || sent;
  const requiresComplianceReview = (taskDescription ?? "")
    .split(";")
    .some((entry) => {
      const separator = entry.indexOf("=");
      return (
        separator > 0 &&
        entry.slice(0, separator).trim().toLowerCase() === "compliance" &&
        entry.slice(separator + 1).trim() !== "0"
      );
    });

  const sendToSigning = async () => {
    if (
      !onSendToSigning ||
      !attachment ||
      signingComplete ||
      !selectedSignees.length ||
      busy ||
      submittingRef.current
    )
      return;
    submittingRef.current = true;
    setSubmitting(true);
    try {
      const pdfData = await fetchAttachment(attachment.taskId, attachment.name);
      const pdfBytes = pdfData?.startsWith("data:")
        ? pdfData.slice(pdfData.indexOf(",") + 1)
        : pdfData;
      if (!pdfBytes)
        throw new Error(
          "Vedlegget mangler PDF-data. Kunne ikke sende til signering."
        );
      if (await onSendToSigning(selectedSignees, pdfBytes)) setSent(true);
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Kunne ikke sende til signering."
      );
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  return (
    <section
      aria-label="Forhåndsvisning av produksjon"
      className="flex h-full min-h-0 flex-col"
      aria-busy={busy}
    >
      <h2 className="flex min-w-0 items-center gap-3 border-b px-4 py-3 text-lg font-semibold">
        <span
          aria-hidden="true"
          className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-red-50 text-red-600 ring-1 ring-inset ring-red-100 dark:bg-red-950/40 dark:text-red-400 dark:ring-red-900"
        >
          <FileText className="size-5" />
        </span>
        <span className="truncate" title={attachment?.name}>
          {attachment?.name ?? "Forhåndsvisning"}
        </span>
      </h2>
      <div className="flex min-h-0 flex-1 flex-col md:flex-row">
        <aside
          aria-label="Dokumenthandlinger"
          className="max-h-[50vh] w-full shrink-0 overflow-y-auto border-b bg-muted/20 md:max-h-none md:w-[380px] md:border-b-0 md:border-r xl:w-[420px]"
        >
          <div className="space-y-4 p-4">
            {busy ? (
              <div
                role="status"
                aria-live="polite"
                className="overflow-hidden rounded-xl border border-blue-200 bg-blue-50/70 dark:border-blue-900 dark:bg-blue-950/30"
              >
                <div className="flex items-start gap-3 p-4">
                  <LoaderCircle
                    aria-hidden="true"
                    className="mt-0.5 size-5 shrink-0 animate-spin text-blue-600 dark:text-blue-400"
                  />
                  <div className="min-w-0">
                    <h3 className="text-sm font-semibold">
                      {signingRequested
                        ? "Forespørselen er sendt – henter signeringslenker"
                        : "Sender dokumentet til signering"}
                    </h3>
                    <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                      {signingRequested
                        ? "Vi venter på resultatet fra signeringsprosessen. Hold vinduet åpent – signeringslenkene vises automatisk når de er klare."
                        : "Vent mens dokumentet klargjøres og forespørselen sendes."}
                    </p>
                  </div>
                </div>
                <LinearProgress aria-label="Behandler signeringsforespørsel" />
              </div>
            ) : signingComplete ? (
              <p
                role="status"
                className="flex items-center gap-2 rounded-xl bg-emerald-50 px-3 py-3 text-sm text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400"
              >
                <Check aria-hidden="true" className="size-4 shrink-0" />
                Signeringsforespørselen er sendt
              </p>
            ) : onSendToSigning ? (
              <>
                {requiresComplianceReview && (
                  <div
                    id="production-preview-compliance-warning"
                    role="alert"
                    aria-labelledby="production-preview-compliance-title"
                    className="flex items-start gap-3 rounded-xl border border-red-500 bg-red-600 p-4 text-white shadow-sm dark:bg-red-700"
                  >
                    <span className="relative flex size-10 shrink-0 items-center justify-center rounded-lg bg-white/10 ring-1 ring-inset ring-white/20">
                      <span
                        aria-hidden="true"
                        className="absolute inset-1 rounded-full bg-white/25 motion-safe:animate-ping"
                      />
                      <TriangleAlert
                        aria-hidden="true"
                        className="relative size-6"
                      />
                    </span>
                    <div className="min-w-0">
                      <h3
                        id="production-preview-compliance-title"
                        className="text-sm font-semibold"
                      >
                        Compliance-kontroll før signering
                      </h3>
                      <p className="mt-1 text-xs leading-relaxed">
                        Denne produksjonen vil havne i compliance-kontroll dersom
                        du velger å sende til signering.
                      </p>
                    </div>
                  </div>
                )}
                <section
                  id="production-signing-details"
                  aria-labelledby="production-signees-title"
                  className="rounded-2xl border border-primary/20 bg-card shadow-sm"
                >
                  <div className="flex items-center gap-3 rounded-t-2xl border-b border-primary/10 bg-primary/5 px-4 py-3">
                    <span
                      aria-hidden="true"
                      className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary"
                    >
                      <Pencil className="size-4" />
                    </span>
                    <div className="min-w-0">
                      <h3
                        id="production-signees-title"
                        className="text-sm font-semibold"
                      >
                        Send til signering
                      </h3>
                    </div>
                  </div>
                  <fieldset
                    disabled={busy}
                    className="m-0 min-w-0 border-0 p-0 disabled:opacity-60 [&>div]:w-full [&>div]:border-r-0 [&_#signee-search-results]:w-full"
                  >
                    <legend className="sr-only">Signatarer</legend>
                    <SigneeSelector
                      signees={signees}
                      selectedSignees={selectedSignees}
                      onChange={(next) => {
                        if (!busy) setSelectedSignees(next);
                      }}
                    />
                  </fieldset>
                  <div className="flex flex-col gap-2 border-t px-4 py-3">
                    <Button
                      type="button"
                      size="sm"
                      className="h-9 rounded-lg normal-case"
                      icon={<Send aria-hidden="true" className="size-4" />}
                      loading={busy}
                      disabled={busy || !attachment || !selectedSignees.length}
                      aria-describedby={
                        requiresComplianceReview
                          ? "production-preview-compliance-warning"
                          : undefined
                      }
                      onClick={() => void sendToSigning()}
                    >
                      Send til signering
                    </Button>
                  </div>
                </section>
              </>
            ) : null}
            {attachment && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-10 w-full rounded-xl normal-case"
                icon={<Download aria-hidden="true" className="size-4" />}
                onClick={() => onDownload(attachment.taskId, attachment.name)}
              >
                Last ned PDF
              </Button>
            )}
          </div>
        </aside>
        <div className="min-h-0 min-w-0 flex-1">
          <ProductionPdfPreview
            attachment={attachment}
            fetchAttachment={fetchAttachment}
          />
        </div>
      </div>
    </section>
  );
}
